import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "../../../../components/forms/Select/Select";
import { Button } from "../../../../components/forms/Buttons/Button";
import { getSubjects } from "../../../../services/Core/SubjectsService";
import { Controller, useForm } from "react-hook-form";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

// `lockedSubject` ({uuid, name}): created inside an exam's subject, the questions take its subject.
// `defaults` ({grade, subject}): what was picked before, when the step is opened again. `back` leaves the form.
export default function SelectClassAndLesson({ next, back, handleData, lockedSubject = null, defaults = null }) {
  const [t] = useTranslation();
  const [grades] = useState([
    { value: -1, labelKey: "my_questions.create.teachers" },
    ...Array.from({ length: 12 }, (_, i) => ({ value: i + 1, labelKey: "my_questions.create.grade_n" })),
  ]);
  const [lessons, setLessons] = useState([]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: { grade: defaults?.grade, subject: defaults?.subject } });

  // The folder loads after the form: put its subject in once it is known.
  useEffect(() => {
    if (lockedSubject?.uuid) setValue("subject", lockedSubject.uuid, { shouldValidate: false });
  }, [lockedSubject?.uuid, setValue]);

  useEffect(() => {
    getSubjects().then((res) => {
      setLessons(Array.isArray(res) ? res : res.results || []);
    });
  }, []);

  const onSubmit = (data) => {
    handleData(data);
    next();
  };

  return (
    <form
      className="flex flex-col pt-4 sm:pt-10"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex w-full max-w-md flex-col gap-3 self-center rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <span className="text-lg font-semibold tracking-tight text-slate-900">
          {t("my_questions.create.select_class_and_lesson")}
        </span>
        <span className="-mt-2 mb-1 text-sm text-slate-500">
          {t("my_questions.create.select_class_and_lesson_tip")}
        </span>
        <Controller
          control={control}
          name="grade"
          rules={{ required: t("my_questions.create.grade_required") }}
          render={({ field }) => (
            <Select
              className="w-full"
              value={field.value}
              onChange={(v) => field.onChange(v.value)}
              errorMsg={errors.grade?.message}
              options={grades.map((g) => ({ value: g.value, label: t(g.labelKey, { grade: g.value }) }))}
            />
          )}
        />
        <Controller
          control={control}
          name="subject"
          rules={{ required: t("my_questions.create.subject_required") }}
          render={({ field }) => (
            <Select
              className="w-full"
              value={field.value}
              onChange={(v) => field.onChange(v.value)}
              errorMsg={errors.subject?.message}
              isDisabled={!!lockedSubject}
              options={
                lockedSubject && !lessons.some((lesson) => lesson.uuid === lockedSubject.uuid)
                  ? [{ value: lockedSubject.uuid, label: lockedSubject.name }]
                  : lessons.map((lesson) => ({
                      value: lesson.uuid,
                      label: lesson.name,
                    }))
              }
            />
          )}
        />
        {/* Phones: the whole width, 44px, as the next steps' buttons; Back under Next. */}
        <div className="mt-3 flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-between">
          {back && (
            <Button className="max-sm:h-11 max-sm:w-full" text={t("buttons.back")} Icon={ArrowLeftIcon} color="secondary-outline" type="button" onClick={back} />
          )}
          <Button className="max-sm:h-11 max-sm:w-full" text={t("buttons.next")} color="primary" type="submit" />
        </div>
      </div>
    </form>
  );
}
